import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Card, Typography, Spin, Divider, List, Avatar, Tag, Button, Image } from 'antd'
import { PrinterOutlined, RightOutlined } from '@ant-design/icons'
import { apiUrl } from '../config/api'
import { formatAmount } from '../utils/amount'

const { Title, Text, Paragraph } = Typography

export default function OrderDetails() {
    const { orderId, subOrderId } = useParams()
    const navigate = useNavigate()

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['order-public', orderId, subOrderId],
        queryFn: async () => {
            const url = apiUrl(`/api/orders/public/${orderId}${subOrderId ? '/' + subOrderId : ''}`)
            const res = await fetch(url)
            if (!res.ok) throw new Error('الطلب غير موجود أو غير متاح')
            return res.json()
        }
    })

    if (isLoading) return <div style={{ display: 'grid', placeItems: 'center', minHeight: '80vh' }}><Spin size="large" tip="جاري تحميل التفاصيل..." /></div>
    if (isError) return (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '80vh', padding: 24 }}>
            <Card style={{ textAlign: 'center', maxWidth: 400 }}>
                <Text type="danger" style={{ fontSize: 18, display: 'block', marginBottom: 16 }}>{error.message}</Text>
                <Button onClick={() => window.location.reload()}>إعادة المحاولة</Button>
            </Card>
        </div>
    )

    const order = data
    const isSubView = !!subOrderId

    return (
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '16px 16px 80px 16px', direction: 'rtl' }}>

            {/* Top Navigation Row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0 }}>تفاصيل الطلب #{order.id}</Title>
                <Button
                    icon={<RightOutlined />}
                    onClick={() => navigate('/categories')}
                    className="no-print"
                >
                    رجوع
                </Button>
            </div>

            {/* Header Card */}
            <Card
                style={{
                    marginBottom: 16,
                    borderTop: '6px solid #f55905',
                    borderRadius: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                        <Tag color="#f55905" style={{ margin: 0, padding: '4px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600 }}>
                            {order.status === 'new' ? 'طلب جديد' : order.status}
                        </Tag>
                        {isSubView && <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 14 }}>عرض لطلب متجر محدد</Text></div>}
                    </div>
                    <Button
                        icon={<PrinterOutlined />}
                        onClick={() => window.print()}
                        className="no-print"
                    >
                        طباعة
                    </Button>
                </div>
            </Card>

            {/* Delivery Info */}
            <Card
                title={<Text strong style={{ fontSize: 16 }}>📍 معلومات التوصيل</Text>}
                style={{ marginBottom: 16, borderRadius: 12 }}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
                    <div>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>اسم العميل:</Text>
                        <Text strong style={{ fontSize: 16 }}>{order.user?.name || '-'}</Text>
                    </div>
                    {!isSubView && <>
                        <div>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>رقم الهاتف:</Text>
                            <Text strong style={{ fontSize: 16, direction: 'ltr', display: 'inline-block' }}>{order.user?.phone || '-'}</Text>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>عنوان التوصيل (منطقة):</Text>
                            <Text strong style={{ fontSize: 16 }}>{order.user?.deliveryLocation?.name || '-'}</Text>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>العنوان التفصيلي:</Text>
                            <Paragraph style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>{order.address || '-'}</Paragraph>
                        </div>
                    </>}

                    {order.notes && (
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>ملاحظات إضافية:</Text>
                            <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', background: '#f9f9f9', padding: 12, borderRadius: 8 }}>
                                {order.notes}
                            </Paragraph>
                        </div>
                    )}
                </div>
            </Card>

            {/* Sub-Orders (Shops) */}
            <Title level={4} style={{ marginBottom: 12, marginTop: 24 }}>📦 محتويات الطلب</Title>

            {order.subOrders?.map(sub => (
                <Card
                    key={sub.id}
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 8, height: 24, background: '#f55905', borderRadius: 4 }} />
                            <Text strong style={{ fontSize: 18 }}>{sub.displayNameAr || sub.shopNameAr} - طلب فرعي رقم {sub.id}</Text>
                        </div>
                    }
                    style={{ marginBottom: 16, borderRadius: 12, overflow: 'hidden' }}
                    bodyStyle={{ padding: 0 }}
                >
                    <List
                        itemLayout="horizontal"
                        dataSource={sub.items}
                        renderItem={item => (
                            <List.Item style={{ padding: '16px' }}>
                                <List.Item.Meta
                                    avatar={
                                        <Image
                                            width={72}
                                            height={72}
                                            src={item.imageUrl ? `https://images.leen-go.com/${item.imageUrl}` : '/no-image.png'}
                                            fallback="/no-image.png"
                                            style={{ border: '1px solid #eee', objectFit: 'cover', borderRadius: 8 }}
                                        />
                                    }
                                    title={<Text style={{ fontSize: 16 }}>{item.itemNameAr}</Text>}
                                    description={
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 8 }}>
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 14 }}>
                                                    سعر الوحدة: {formatAmount(item.unitPrice)}
                                                </Text>
                                                <div style={{ marginTop: 4 }}>
                                                    <Tag style={{ margin: 0 }}>الكمية: {item.quantity}</Tag>
                                                </div>
                                            </div>
                                            <Text strong style={{ fontSize: 16, color: '#333' }}>{formatAmount(item.rowTotal)}</Text>
                                        </div>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                    <div
                        style={{
                            padding: '12px 16px',
                            borderTop: '1px solid #f0f0f0',
                            textAlign: 'left',
                            background: '#fafafa',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: 8
                        }}
                    >
                        <Text type="secondary">إجمالي المتجر: </Text>
                        <Text strong style={{ fontSize: 18, color: '#f55905' }}>{formatAmount(sub.totalAfterDiscount)}</Text>
                    </div>
                </Card>
            ))}

            {/* Summary / Total Card */}
            {!isSubView && (
                <Card
                    style={{
                        marginTop: 24,
                        background: '#fff7f2',
                        border: '2px dashed #f55905',
                        borderRadius: 12
                    }}
                >
                    <div style={{ display: 'grid', gap: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text style={{ fontSize: 16 }}>رسوم التوصيل:</Text>
                            <Text style={{ fontSize: 16 }}>{formatAmount(order.deliveryFee)}</Text>
                        </div>
                        <Divider style={{ margin: '8px 0', borderColor: '#f5590544' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Title level={3} style={{ margin: 0 }}>الإجمالي الكلي</Title>
                            <Title level={2} style={{ margin: 0, color: '#f55905' }}>{formatAmount(order.totalAfterDiscount)}</Title>
                        </div>
                    </div>
                </Card>
            )}

            {isSubView && (
                <div style={{ textAlign: 'center', marginTop: 32, opacity: 0.6 }}>
                    <Text type="secondary">هذا رابط مشاركة مخصص لهذا المتجر فقط</Text>
                </div>
            )}

            {/* Branding Footer */}
            <div style={{ textAlign: 'center', marginTop: 48, paddingBottom: 24 }}>
                <img src="/logo.png" alt="Leen Mall" style={{ height: 40, opacity: 0.7, marginBottom: 8 }} />
                <div style={{ color: '#aaa', fontSize: 12 }}>© {new Date().getFullYear()} لين غو - تجربة تسوق متكاملة</div>
            </div>

            <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .ant-card { box-shadow: none !important; border: 1px solid #eee !important; }
        }
      `}</style>
        </div>
    )
}
